import { useState, useEffect } from 'react';
import { OnboardingState } from '../OnboardingWizard';
import { assignAdminAgents } from '../../../services/api';
import {
  Settings,
  CheckCircle,
  RefreshCw,
  AlertTriangle,
  Users,
} from 'lucide-react';

interface Props {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
  onNext: () => void;
  setError: (error: string | null) => void;
}

export default function FinalizeSetupStep({ state, updateState, onNext, setError }: Props) {
  const [loading, setLoading] = useState(false);
  const [assigned, setAssigned] = useState(state.adminAgentsAssigned);
  const [assignedRoles, setAssignedRoles] = useState<string[]>([]);

  useEffect(() => {
    // If already assigned in a previous visit to this step, don't re-assign
    if (state.adminAgentsAssigned) {
      setAssigned(true);
    }
  }, [state.adminAgentsAssigned]);

  const handleAssign = async () => {
    if (!state.relationshipId) {
      setError('No GDAP relationship ID found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await assignAdminAgents(state.relationshipId);

      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }

      setAssignedRoles(result.roles || []);
      updateState({
        adminAgentsAssigned: true,
      });
      setAssigned(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign AdminAgents');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Settings className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Finalize Setup
            </h2>
            <p className="text-sm text-muted-foreground">
              Assign AdminAgents security group to the GDAP relationship
            </p>
          </div>
        </div>
      </div>

      {!assigned ? (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
              What happens in this step:
            </h3>
            <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
              <li className="flex items-start">
                <span className="mr-2">1.</span>
                The AdminAgents security group will be assigned to the GDAP relationship
              </li>
              <li className="flex items-start">
                <span className="mr-2">2.</span>
                This enables delegated admin access to the customer tenant
              </li>
              <li className="flex items-start">
                <span className="mr-2">3.</span>
                All 26 admin roles will be activated for the partner
              </li>
            </ul>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start">
              <Users className="h-5 w-5 text-muted-foreground mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  AdminAgents Security Group
                </h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  This group contains the partner users who will have delegated admin access to manage the customer's Microsoft 365 tenant.
                </p>
              </div>
            </div>
          </div>

          {state.customerTenantId && (
            <div className="bg-muted rounded-lg p-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-2">
                Customer Information
              </h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Customer Name</dt>
                  <dd className="font-medium text-foreground">
                    {state.customerDisplayName || state.companyName}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Tenant ID</dt>
                  <dd className="font-mono text-foreground text-xs">
                    {state.customerTenantId}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          <button
            onClick={handleAssign}
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Assigning AdminAgents...
              </>
            ) : (
              'Assign AdminAgents Security Group'
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <h3 className="font-medium text-green-800 dark:text-green-200">
                AdminAgents Successfully Assigned
              </h3>
            </div>
            <p className="mt-2 text-sm text-green-700 dark:text-green-300">
              The AdminAgents security group has been assigned to the GDAP relationship. Delegated admin access is now active.
            </p>
          </div>

          {assignedRoles.length > 0 && (
            <div className="bg-muted rounded-lg p-4">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Activated Roles ({assignedRoles.length}):
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-muted-foreground">
                {assignedRoles.slice(0, 6).map((role) => (
                  <div key={role} className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    {role}
                  </div>
                ))}
                {assignedRoles.length > 6 && (
                  <div className="text-muted-foreground">
                    +{assignedRoles.length - 6} more roles
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Next Step Required
                </h4>
                <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  The customer must now grant consent for the Reports application to access their tenant data for security scanning and reporting.
                </p>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <button onClick={onNext} className="btn-primary w-full sm:w-auto">
              Continue to App Consent &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
